import { createGuid, isNullOrUndefined } from '@bimser/core';
import {
	changeModalProps,
	closeModalAction,
	openModalAction,
	updateModalButton
} from '../components/ModalManager/actions';
import { IModalState } from '../components/ModalManager/entities';
import { MessageBoxIcon } from '../components/ModalManager/enums';
import {
	openMessageBoxTransactionAction,
	resolveTransactionAction
} from '../components/ModalManager/modals/MessageBox/actions';
import {
	IMessageBoxBaseRequest,
	IMessageBoxButton,
	IMessageBoxRequest,
	IMessageBoxResolvePayload
} from '../components/ModalManager/modals/MessageBox/entities';

declare global {
	interface Window {
		__store: any;
	}
}

function show(request: IMessageBoxRequest) {
	return new Promise<IMessageBoxResolvePayload>((resolve, reject) => {
		if (!window.__store) {
			throw 'Store cannot be null';
		}

		const _id = isNullOrUndefined(request.id) ? createGuid() : request.id;

		const action = openMessageBoxTransactionAction({
			id: _id,
			title: request.title,
			content: request.content,
			type: isNullOrUndefined(request.messageType)
				? MessageBoxIcon.None
				: request.messageType,
			buttons: request.buttons,
			width: request.width,
			errorDetail: request.errorDetail,
			errorCode: request.errorCode,
			error: request.error,
			showErrorDetail: request.showErrorDetail,
			onReturn: (promise: Promise<IMessageBoxResolvePayload>) => {
				promise.then(response => {
					resolve(response);
				});
			},
			props: request.props,
			notInCloseIcon: request.notInCloseIcon,
			canFullScreen: request.canFullScreen
		});

		window.__store.dispatch(action);
	});
}

export function MessageBoxManager() {
	return {
		show: (request: IMessageBoxRequest) =>
			show({
				id: request.id,
				title: request.title,
				width: request.width,
				content: request.content,
				buttons: request.buttons,
				messageType: request.messageType,
				props: request.props,
				notInCloseIcon: request.notInCloseIcon,
				canFullScreen: request.canFullScreen
			}),
		info: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Info,
				props: request.props,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		error: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Error,
				props: request.props,
				errorDetail: request.errorDetail,
				showErrorDetail: request.showErrorDetail,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon,
				errorCode: request.errorCode,
				error: request.error
			}),
		warning: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Warning,
				errorDetail: request.errorDetail,
				showErrorDetail: request.showErrorDetail,
				props: request.props,
				width: request.width || 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		success: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Success,
				props: request.props,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		severeWarning: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.SevereWarning,
				props: request.props,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		Blocked: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Blocked,
				props: request.props,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		custom: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.None,
				props: request.props,
				notInCloseIcon: request.notInCloseIcon,
				width: request.width
			}),
		confirm: (request: IMessageBoxBaseRequest) =>
			show({
				id: null,
				title: request.title,
				content: request.content,
				buttons: request.buttons,
				messageType: MessageBoxIcon.Confirm,
				props: request.props,
				width: 'default',
				notInCloseIcon: request.notInCloseIcon
			}),
		closeModal: (windowId: string) => {
			window.__store.dispatch(closeModalAction(windowId));
		},
		openModal: (data: IModalState) => {
			window.__store.dispatch(openModalAction(data));
		},
		resolveModal: (data: IMessageBoxResolvePayload) => {
			window.__store.dispatch(resolveTransactionAction(data));
		},
		toggleLoading: (id: string, isLoading: boolean) => {
			window.__store.dispatch(changeModalProps({ id, data: { isLoading } }));
		},
		updateButton: (id: string, button: IMessageBoxButton) => {
			window.__store.dispatch(updateModalButton({ modalId: id, data: button }));
		}
	};
}

export default MessageBoxManager();
